import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Stack,
    Button,
    CircularProgress,
    Alert,
    Tooltip,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TableSortLabel,
    Snackbar,
    Switch,
    TextField,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate } from "react-router-dom";
import {
    getAllUsers,
    runAnalyzerForAllUsers,
    updateUserMailingEnabled,
} from "../api/AiAgentApi";

function descendingComparator(a, b, orderBy) {
    const va = a?.[orderBy];
    const vb = b?.[orderBy];
    if (vb === undefined || vb === null) return -1;
    if (va === undefined || va === null) return 1;
    if (vb < va) return -1;
    if (vb > va) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

const toSearchable = (value) => String(value ?? "").toLowerCase();

export const UsersPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");

    const [runningAll, setRunningAll] = useState(false);
    const [appliedSearch, setAppliedSearch] = useState("");
    const [mailingUpdatingById, setMailingUpdatingById] = useState({});
    const [snack, setSnack] = useState({ open: false, msg: "" });
    const searchInputRef = useRef(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setErr(null);
            const res = await getAllUsers();
            setRows(res ?? []);
        } catch (e) {
            setErr(e?.message || "Не удалось загрузить пользователей");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const filteredRows = useMemo(() => {
        const q = appliedSearch.trim().toLowerCase();
        if (!q) return rows;

        return rows.filter((r) => {
            const mailingLabel = r?.mailingEnabled ? "enabled" : "disabled";
            return (
                toSearchable(r?.id).includes(q) ||
                toSearchable(r?.telegramId).includes(q) ||
                toSearchable(r?.amoCrmContactId).includes(q) ||
                mailingLabel.includes(q)
            );
        });
    }, [rows, appliedSearch]);

    const sortedRows = useMemo(
        () => stableSort(filteredRows, getComparator(order, orderBy)),
        [filteredRows, order, orderBy]
    );

    const runAll = async () => {
        try {
            setRunningAll(true);
            await runAnalyzerForAllUsers();
            alert("Success");
        } catch (e) {
            setSnack({ open: true, msg: e?.message || "Не удалось запустить анализ" });
        } finally {
            setRunningAll(false);
        }
    };

    const handleToggleMailing = async (userId, nextValue) => {
        if (!userId) return;

        setMailingUpdatingById((prev) => ({ ...prev, [userId]: true }));
        setRows((prev) => prev.map((item) => (item.id === userId ? { ...item, mailingEnabled: nextValue } : item)));

        try {
            await updateUserMailingEnabled(userId, nextValue);
        } catch (e) {
            setRows((prev) => prev.map((item) => (item.id === userId ? { ...item, mailingEnabled: !nextValue } : item)));
            setSnack({ open: true, msg: e?.message || "Не удалось изменить mailing-enabled" });
        } finally {
            setMailingUpdatingById((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const applySearch = () => {
        setAppliedSearch(searchInputRef.current?.value ?? "");
    };

    return (
        <Box sx={{ p: 2, width: "100%" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1 }}>
                <Tooltip title="Запустить анализ для всех пользователей">
          <span>
            <Button
                variant="contained"
                color="success"
                startIcon={runningAll ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={runAll}
                disabled={runningAll}
            >
              Запустить анализ для всех
            </Button>
          </span>
                </Tooltip>

                <Button onClick={fetchData} disabled={loading}>
                    {loading ? <CircularProgress size={18} /> : "Обновить"}
                </Button>

                <TextField
                    size="small"
                    label="Поиск"
                    inputRef={searchInputRef}
                    defaultValue={appliedSearch}
                    placeholder="ID / Telegram / amoCRM"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            applySearch();
                        }
                    }}
                    sx={{ minWidth: { xs: "100%", sm: 280 }, ml: { xs: 0, sm: "auto" } }}
                />
                <Button onClick={applySearch} disabled={loading}>
                    Поиск
                </Button>
            </Stack>

            {err && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {err}
                </Alert>
            )}

            <Paper variant="outlined">
                <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={orderBy === "id" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "id"}
                                        direction={orderBy === "id" ? order : "asc"}
                                        onClick={() => handleRequestSort("id")}
                                    >
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === "telegramId" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "telegramId"}
                                        direction={orderBy === "telegramId" ? order : "asc"}
                                        onClick={() => handleRequestSort("telegramId")}
                                    >
                                        Telegram ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === "amoCrmContactId" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "amoCrmContactId"}
                                        direction={orderBy === "amoCrmContactId" ? order : "asc"}
                                        onClick={() => handleRequestSort("amoCrmContactId")}
                                    >
                                        amoCRM Contact
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === "mailingEnabled" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "mailingEnabled"}
                                        direction={orderBy === "mailingEnabled" ? order : "asc"}
                                        onClick={() => handleRequestSort("mailingEnabled")}
                                    >
                                        Рассылка включена
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">Действия</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={22} />
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && sortedRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        Нет данных
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading &&
                                sortedRows.map((r) => (
                                    <TableRow
                                        key={r.id}
                                        hover
                                        sx={{ cursor: r?.id ? "pointer" : "default" }}
                                        onClick={() => r?.id && navigate(`/users/${r.id}/analyzer`)}
                                    >
                                        <TableCell>{r?.id ?? "—"}</TableCell>
                                        <TableCell>{r?.telegramId ?? "—"}</TableCell>
                                        <TableCell>{r?.amoCrmContactId ?? "—"}</TableCell>
                                        <TableCell>
                                            <Tooltip title={r?.mailingEnabled ? "Отключить рассылку" : "Включить рассылку"}>
                                                <span>
                                                    <Switch
                                                        checked={Boolean(r?.mailingEnabled)}
                                                        color="success"
                                                        disabled={!r?.id || Boolean(mailingUpdatingById[r?.id])}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => handleToggleMailing(r?.id, e.target.checked)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Перейти на анализ пользователя">
                        <span>
                          <Button
                              size="small"
                              variant="contained"
                              startIcon={<AnalyticsIcon />}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/users/${r.id}/analyzer`);
                              }}
                              disabled={!r?.id}
                          >
                            Анализ
                          </Button>
                        </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                message={snack.msg}
            />
        </Box>
    );
};

export default UsersPage;
